/**
 * Region → language guess, derived from the device's IANA timezone.
 *
 * <p>This is deliberately NOT IP geolocation. The browser already knows which
 * timezone the device is set to, so reading it costs nothing, sends nothing
 * over the network, and stores nothing — which keeps it out of the cookie and
 * privacy notices entirely. It is also steadier than an IP lookup: a VPN moves
 * your apparent country, but not your clock.</p>
 *
 * <p>It only ever guesses. The detector that consumes this sits behind the
 * saved-choice lookup, so a visitor who has touched the language switcher is
 * never second-guessed, and returning {@code undefined} simply falls through to
 * the browser's own language preference.</p>
 */

/**
 * Timezones where Danish is the better opening guess than English.
 *
 * <p>Denmark proper, plus the rest of the realm: Faroese and Greenlandic
 * speakers are far more likely to read Danish than English, and neither
 * language is one we ship. Legacy zone names are included because older
 * browsers still report them (the OS alias is not always resolved).</p>
 */
const DANISH_TIMEZONES: ReadonlySet<string> = new Set([
  // Denmark
  "Europe/Copenhagen",
  // Faroe Islands ("Faeroe" is the pre-2011 spelling)
  "Atlantic/Faroe",
  "Atlantic/Faeroe",
  // Greenland ("Godthab" was renamed to "Nuuk" in 2020)
  "America/Nuuk",
  "America/Godthab",
  "America/Danmarkshavn",
  "America/Scoresbysund",
  "America/Thule",
]);

/**
 * Maps a timezone to a supported language code, or undefined when the region
 * tells us nothing useful.
 *
 * @param timezone an IANA zone name, e.g. "Europe/Copenhagen"
 */
export function languageForTimezone(
  timezone: string | undefined | null,
): "da" | undefined {
  if (!timezone) return undefined;
  return DANISH_TIMEZONES.has(timezone) ? "da" : undefined;
}

/**
 * Reads the device timezone and maps it. Guarded because `Intl` support and
 * the shape of `resolvedOptions()` vary across older browsers, and a failed
 * language guess must never take the page down.
 */
export function languageForDeviceRegion(): "da" | undefined {
  try {
    return languageForTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  } catch {
    return undefined;
  }
}
